import {
  Box,
  Card,
  Grid,
  Stack,
  Typography,
  Switch,
  FormControlLabel,
  TextField as MuiTextField,
  Button,
  Alert,
  Tab,
  Tabs,
} from '@mui/material';
import { useState, useEffect, useCallback } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import { CustomBreadcrumbs } from '../../../../../components/custom-breadcrumbs';
import { DashboardContent } from '../../../../../layouts/dashboard';
import { Resources } from '../../resource';
import { PermissionGuard } from '../../../../../routes/components/permission-guard';
import { PermissionButton } from '../../../../../routes/components/permission-button';
import ErrorPage403 from '../../../../error/403';
import {
  bulkUpdateFeatureFlags,
  getFeatureFlags,
  FeatureFlags,
  BulkUpdateFeatureFlagsInput,
  FeatureFlag,
} from '../../../services/redpacketService';

type FeatureFlagField =
  | 'v2Enabled'
  | 'leaderboardEnabled'
  | 'luckyLottoEnabled'
  | 'overlayEnabled'
  | 'claimEnabledH5'
  | 'claimEnabledWeb'
  | 'claimEnabledMobile'
  | 'withdrawalEnabledH5'
  | 'withdrawalEnabledWeb'
  | 'withdrawalEnabledMobile';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`feature-flags-tabpanel-${index}`}
      aria-labelledby={`feature-flags-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

// H5 与 Web 保持一致，取其一作为统一值（优先 H5）
const getInitialFormData = (status: FeatureFlags | null): BulkUpdateFeatureFlagsInput => {
  const claimH5Web = status?.claimEnabledH5 ?? status?.claimEnabledWeb ?? false;
  const withdrawalH5Web = status?.withdrawalEnabledH5 ?? status?.withdrawalEnabledWeb ?? false;
  return {
    v2Enabled: status?.v2Enabled,
    leaderboardEnabled: status?.leaderboardEnabled,
    luckyLottoEnabled: status?.luckyLottoEnabled,
    claimEnabledH5: claimH5Web,
    claimEnabledWeb: claimH5Web,
    claimEnabledMobile: status?.claimEnabledMobile,
    withdrawalEnabledH5: withdrawalH5Web,
    withdrawalEnabledWeb: withdrawalH5Web,
    withdrawalEnabledMobile: status?.withdrawalEnabledMobile,
    reason: '',
  };
};

const FeatureFlagsList = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [formData, setFormData] = useState<BulkUpdateFeatureFlagsInput>(getInitialFormData(null));
  const [currentStatus, setCurrentStatus] = useState<FeatureFlags | null>(null);
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [successMessage, setSuccessMessage] = useState<string>('');

  const handleSwitchChange =
    (field: FeatureFlagField) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;
      setFormData((prev) => {
        // 关闭时校验：必须至少有一个设备（App 或 Web/H5）处于开启状态
        if (!checked) {
          const claimWeb = prev.claimEnabledH5 ?? prev.claimEnabledWeb ?? (currentStatus as any)?.claimEnabledH5 ?? false;
          const claimMobile = prev.claimEnabledMobile ?? (currentStatus as any)?.claimEnabledMobile ?? false;
          const withdrawalWeb = prev.withdrawalEnabledH5 ?? prev.withdrawalEnabledWeb ?? (currentStatus as any)?.withdrawalEnabledH5 ?? false;
          const withdrawalMobile = prev.withdrawalEnabledMobile ?? (currentStatus as any)?.withdrawalEnabledMobile ?? false;

          const isClaimField = ['claimEnabledH5', 'claimEnabledWeb', 'claimEnabledMobile'].includes(field);
          const isWithdrawalField = ['withdrawalEnabledH5', 'withdrawalEnabledWeb', 'withdrawalEnabledMobile'].includes(field);

          if (isClaimField) {
            const otherClaimOn = field === 'claimEnabledMobile' ? claimWeb : claimMobile;
            if (!otherClaimOn) {
              notify(translate('module.redpacket.atLeastOneDeviceOpen'), { type: 'warning' });
              return prev;
            }
          }
          if (isWithdrawalField) {
            const otherWithdrawalOn = field === 'withdrawalEnabledMobile' ? withdrawalWeb : withdrawalMobile;
            if (!otherWithdrawalOn) {
              notify(translate('module.redpacket.atLeastOneDeviceOpen'), { type: 'warning' });
              return prev;
            }
          }
        }

        const next = { ...prev, [field]: checked };
        // H5 与 Web 保持一致，请求接口时两个同时 true/false
        if (field === 'claimEnabledH5' || field === 'claimEnabledWeb') {
          next.claimEnabledH5 = checked;
          next.claimEnabledWeb = checked;
        } else if (field === 'withdrawalEnabledH5' || field === 'withdrawalEnabledWeb') {
          next.withdrawalEnabledH5 = checked;
          next.withdrawalEnabledWeb = checked;
        }
        return next;
      });
    };

  const handleReasonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      reason: event.target.value,
    }));
  };

  const fetchCurrentFlags = useCallback(async () => {
    try {
      setFetching(true);
      const response = await getFeatureFlags();
      setCurrentStatus(response.status);
      setFlags(response.flags || []);
      setFormData(getInitialFormData(response.status));
    } catch (error: any) {
      notify(error.message || 'Failed to fetch feature flags', { type: 'error' });
    } finally {
      setFetching(false);
    }
  }, [notify]);

  const handleSubmit = useCallback(async () => {
    const status = currentStatus as any;
    const claimWeb = formData.claimEnabledH5 ?? formData.claimEnabledWeb ?? status?.claimEnabledH5 ?? status?.claimEnabledWeb ?? false;
    const claimMobile = formData.claimEnabledMobile ?? status?.claimEnabledMobile ?? false;
    const withdrawalWeb = formData.withdrawalEnabledH5 ?? formData.withdrawalEnabledWeb ?? status?.withdrawalEnabledH5 ?? status?.withdrawalEnabledWeb ?? false;
    const withdrawalMobile = formData.withdrawalEnabledMobile ?? status?.withdrawalEnabledMobile ?? false;

    if (!claimWeb && !claimMobile) {
      notify('Open RedPacket: At least one device (App or Web/H5) needs to be left open', { type: 'error' });
      return;
    }
    if (!withdrawalWeb && !withdrawalMobile) {
      notify('Claim USDC Reward: At least one device (App or Web/H5) needs to be left open', { type: 'error' });
      return;
    }

    try {
      setLoading(true);
      setSuccessMessage('');
      const updateData: BulkUpdateFeatureFlagsInput = {};
      const fields: FeatureFlagField[] = [
        'v2Enabled',
        'leaderboardEnabled',
        'luckyLottoEnabled',
        'overlayEnabled',
        'claimEnabledH5',
        'claimEnabledWeb',
        'claimEnabledMobile',
        'withdrawalEnabledH5',
        'withdrawalEnabledWeb',
        'withdrawalEnabledMobile',
      ];
      fields.forEach((f) => {
        if (formData[f] !== undefined) (updateData as any)[f] = formData[f];
      });
      if (formData.reason) updateData.reason = formData.reason;

      const response = await bulkUpdateFeatureFlags(updateData);
      setCurrentStatus(response);
      notify(translate('module.redpacket.featureFlagsUpdatedSuccess'), { type: 'success' });
      setSuccessMessage(translate('module.redpacket.featureFlagsUpdatedSuccessMessage'));
      await fetchCurrentFlags();
      setFormData(getInitialFormData(response));
    } catch (error: any) {
      notify(error.message || 'Failed to update feature flags', { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [formData, notify, fetchCurrentFlags, currentStatus,translate]);

  useEffect(() => {
    fetchCurrentFlags();
  }, [fetchCurrentFlags]);

  const handleReset = () => {
    setFormData(getInitialFormData(currentStatus));
    setSuccessMessage('');
  };

  const getChecked = (field: FeatureFlagField) =>
    formData[field] ?? (currentStatus as any)?.[field] ?? false;

  const SwitchRow = ({
    field,
    label,
    description,
  }: {
    field: FeatureFlagField;
    label: string;
    description: string;
  }) => (
    <Grid item xs={12}>
      <FormControlLabel
        control={
          <Switch
            checked={getChecked(field)}
            onChange={handleSwitchChange(field)}
            disabled={loading || fetching}
          />
        }
        label={label}
      />
      <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: 0.5 }}>
        {description}
      </Typography>
    </Grid>
  );

  return (
    <PermissionGuard resource="redpacket" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent>
        <CustomBreadcrumbs
          heading={translate('module.redpacket.featureFlags')}
          links={[
            { name: translate('module.redpacket.redPacketManagement'), href: `/${Resources.redpacket}` },
            { name: translate('module.redpacket.featureFlags') },
          ]}
        />

        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="body2">
            {translate('module.redpacket.v2Note')}
          </Typography>
        </Alert>
        <Card sx={{ mt: 3, p: 3 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs
              value={tabValue}
              onChange={(_e, v) => setTabValue(v)}
              aria-label="Feature flags tabs"
            >
              <Tab label={translate('module.redpacket.v2Tab')} id="feature-flags-tab-0" />
              <Tab label={translate('module.redpacket.openRedPacketClaimTab')} id="feature-flags-tab-1" />
            </Tabs>
          </Box>

          <Typography variant="h6" sx={{ mb: 2 }}>
            {translate('module.redpacket.updateFeatureFlags')}
          </Typography>

          {successMessage && (
            <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage('')}>
              {successMessage}
            </Alert>
          )}

          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={3}>
              <SwitchRow
                field="v2Enabled"
                label={translate('module.redpacket.v2Enabled')}
                description="Enable or disable V2 features"
              />
              <SwitchRow
                field="leaderboardEnabled"
                label={translate('module.redpacket.leaderboardEnabled')}
                description="Enable or disable leaderboard features"
              />
              <SwitchRow
                field="luckyLottoEnabled"
                label={translate('module.redpacket.luckyLottoEnabled')}
                description="Enable or disable lucky lotto features"
              />
              {/*<SwitchRow*/}
              {/*  field="overlayEnabled"*/}
              {/*  label="Overlay Enabled"*/}
              {/*  description="Enable or disable overlay features"*/}
              {/*/>*/}
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                {translate('module.redpacket.atLeastOneDeviceKeepOn')}
              </Typography>
            </Alert>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  {translate('module.redpacket.openRedPacketEnabled')}
                </Typography>
              </Grid>
              <SwitchRow
                field="claimEnabledH5"
                label={translate('module.redpacket.openRedPacketEnabledH5')}
                description={translate('module.redpacket.enableRedPacketH5')}
              />
              <SwitchRow
                field="claimEnabledWeb"
                label={translate('module.redpacket.openRedPacketEnabledWeb')}
                description={translate('module.redpacket.enableRedPacketWeb')}
              />
              <SwitchRow
                field="claimEnabledMobile"
                label={translate('module.redpacket.openRedPacketEnabledMobile')}
                description={translate('module.redpacket.enableRedPacketMobile')}
              />
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  {translate('module.redpacket.claimUsdcRewardEnabled')}
                </Typography>
              </Grid>
              <SwitchRow
                field="withdrawalEnabledH5"
                label={translate('module.redpacket.claimEnabledH5')}
                description={translate('module.redpacket.claimUsdcRewardH5')}
              />
              <SwitchRow
                field="withdrawalEnabledWeb"
                label={translate('module.redpacket.claimEnabledWeb')}
                description={translate('module.redpacket.claimUsdcRewardWeb')}
              />
              <SwitchRow
                field="withdrawalEnabledMobile"
                label={translate('module.redpacket.claimEnabledMobile')}
                description={translate('module.redpacket.claimUsdcRewardMobile')}
              />
            </Grid>
          </TabPanel>

          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12}>
              <MuiTextField
                fullWidth
                label={translate('module.redpacket.changeReasonBtn')}
                placeholder={translate('module.redpacket.changeReasonPlaceholder')}
                value={formData.reason || ''}
                onChange={handleReasonChange}
                disabled={loading || fetching}
                multiline
                rows={3}
              />
            </Grid>

            <Grid item xs={12}>
              <Stack direction="row" spacing={2}>
                <PermissionButton resource="redpacket" action="update">
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading || fetching}
                    size="large"
                  >
                    {loading ? translate('module.redpacket.updating') : translate('module.redpacket.updateFeatureFlags')}
                  </Button>
                </PermissionButton>
                <Button variant="outlined" onClick={handleReset} disabled={loading || fetching}>
                  {translate('module.redpacket.reset')}
                </Button>
              </Stack>
            </Grid>
          </Grid>

          {/*{currentStatus && (*/}
          {/*  <Box sx={{ mt: 4, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>*/}
          {/*    <Typography variant="h6" sx={{ mb: 2 }}>*/}
          {/*      Current Configuration*/}
          {/*    </Typography>*/}
          {/*    <Grid container spacing={2}>*/}
          {/*      {[*/}
          {/*        ['v2Enabled', 'V2 Enabled'],*/}
          {/*        ['leaderboardEnabled', 'Leaderboard Enabled'],*/}
          {/*        ['luckyLottoEnabled', 'Lucky Lotto Enabled'],*/}
          {/*        ['overlayEnabled', 'Overlay Enabled'],*/}
          {/*        ['claimEnabledH5', 'Claim H5'],*/}
          {/*        ['claimEnabledWeb', 'Claim Web'],*/}
          {/*        ['claimEnabledMobile', 'Claim Mobile'],*/}
          {/*        ['withdrawalEnabledH5', 'Withdrawal H5'],*/}
          {/*        ['withdrawalEnabledWeb', 'Withdrawal Web'],*/}
          {/*        ['withdrawalEnabledMobile', 'Withdrawal Mobile'],*/}
          {/*      ].map(([key, label]) => (*/}
          {/*        <Grid item xs={12} md={4} key={key}>*/}
          {/*          <Typography variant="subtitle2" color="text.secondary">*/}
          {/*            {label}*/}
          {/*          </Typography>*/}
          {/*          <Typography variant="body1">*/}
          {/*            {(currentStatus as any)[key] ? 'Enabled' : 'Disabled'}*/}
          {/*          </Typography>*/}
          {/*        </Grid>*/}
          {/*      ))}*/}
          {/*      {currentStatus.envDefaults && (*/}
          {/*        <>*/}
          {/*          <Grid item xs={12}>*/}
          {/*            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>*/}
          {/*              Environment Defaults*/}
          {/*            </Typography>*/}
          {/*          </Grid>*/}
          {/*          {[*/}
          {/*            ['v2Enabled', 'V2'],*/}
          {/*            ['leaderboardEnabled', 'Leaderboard'],*/}
          {/*            ['luckyLottoEnabled', 'Lucky Lotto'],*/}
          {/*            ['overlayEnabled', 'Overlay'],*/}
          {/*            ['claimEnabledH5', 'Claim H5'],*/}
          {/*            ['claimEnabledWeb', 'Claim Web'],*/}
          {/*            ['claimEnabledMobile', 'Claim Mobile'],*/}
          {/*            ['withdrawalEnabledH5', 'Withdrawal H5'],*/}
          {/*            ['withdrawalEnabledWeb', 'Withdrawal Web'],*/}
          {/*            ['withdrawalEnabledMobile', 'Withdrawal Mobile'],*/}
          {/*          ].map(([key, label]) => (*/}
          {/*            <Grid item xs={12} md={4} key={`env-${key}`}>*/}
          {/*              <Typography variant="body2" color="text.secondary">*/}
          {/*                {label} (Default)*/}
          {/*              </Typography>*/}
          {/*              <Typography variant="body2">*/}
          {/*                {(currentStatus.envDefaults as any)[key] ? 'Enabled' : 'Disabled'}*/}
          {/*              </Typography>*/}
          {/*            </Grid>*/}
          {/*          ))}*/}
          {/*        </>*/}
          {/*      )}*/}
          {/*    </Grid>*/}
          {/*  </Box>*/}
          {/*)}*/}

          {/*{flags.length > 0 && (*/}
          {/*  <Box sx={{ mt: 4, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>*/}
          {/*    <Typography variant="h6" sx={{ mb: 2 }}>*/}
          {/*      Feature Flags Details*/}
          {/*    </Typography>*/}
          {/*    <Grid container spacing={2}>*/}
          {/*      {flags.map((flag) => (*/}
          {/*        <Grid item xs={12} md={4} key={flag.id}>*/}
          {/*          <Typography variant="subtitle2" color="text.secondary">*/}
          {/*            {flag.flagKey}*/}
          {/*          </Typography>*/}
          {/*          <Typography variant="body2">*/}
          {/*            Status: {flag.enabled ? 'Enabled' : 'Disabled'}*/}
          {/*          </Typography>*/}
          {/*          {flag.description && (*/}
          {/*            <Typography variant="body2" color="text.secondary">*/}
          {/*              {flag.description}*/}
          {/*            </Typography>*/}
          {/*          )}*/}
          {/*          {flag.updatedBy && (*/}
          {/*            <Typography variant="caption" color="text.secondary">*/}
          {/*              Updated by: {flag.updatedBy}*/}
          {/*            </Typography>*/}
          {/*          )}*/}
          {/*        </Grid>*/}
          {/*      ))}*/}
          {/*    </Grid>*/}
          {/*  </Box>*/}
          {/*)}*/}
        </Card>
      </DashboardContent>
    </PermissionGuard>
  );
};

export default FeatureFlagsList;
